<template>
  <div class="max-w-5xl mx-auto">
    <!-- Success banner (rename feedback, top-level like Billing) -->
    <div
      v-if="nameSaved"
      class="mb-6 p-4 bg-green-900/50 border border-green-600 rounded-lg flex items-center gap-3"
    >
      <span class="material-icons text-green-400">check_circle</span>
      <p class="text-green-300 flex-1">{{ $t('workspace.settings.savedToast') }}</p>
      <Button
        variant="text"
        severity="success"
        rounded
        size="small"
        class="ml-auto"
        v-tooltip.top="$t('common.close')"
        :aria-label="$t('common.close')"
        @click="nameSaved = false"
      >
        <template #icon>
          <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">close</span>
        </template>
      </Button>
    </div>

    <!-- Header -->
    <h1 class="text-3xl font-bold text-white mb-6">{{ $t('workspace.settings.title') }}</h1>

    <!-- Back Button -->
    <BackButton />

    <!-- Loading State -->
    <div v-if="loading && !workspace" class="bg-gray-800 rounded-lg p-12 text-center">
      <div class="flex justify-center mb-4">
        <ProgressSpinner
          style="width: 3rem; height: 3rem"
          stroke-width="3"
          :aria-label="$t('common.loading')"
        />
      </div>
      <p class="text-gray-400">{{ $t('common.loading') }}</p>
    </div>

    <!-- Loaded State -->
    <div v-else-if="workspace" class="space-y-6">
      <!-- Card 1: General -->
      <div class="bg-gray-800 rounded-lg p-6">
        <div class="flex items-center gap-3 mb-4">
          <span class="material-icons text-2xl text-cyan-400">settings</span>
          <h2 class="text-lg font-semibold text-white">{{ $t('workspace.settings.generalTitle') }}</h2>
        </div>
        <p class="text-gray-400 text-sm mb-4">{{ $t('workspace.settings.generalDescription') }}</p>

        <!-- Inner panel for the rename form (Billing pattern) -->
        <div class="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
          <label for="input-workspace-name" class="block text-gray-300 text-sm font-medium mb-3">
            {{ $t('workspace.settings.nameLabel') }}
          </label>
          <div class="flex flex-col sm:flex-row gap-3">
            <!-- The tooltip sits on a wrapper: a disabled input swallows hover events. -->
            <span
              v-tooltip.top="!isOwner ? $t('workspace.settings.ownerOnlyTooltip') : ''"
              class="flex flex-1"
              :class="{ 'disabled-wrap': !isOwner }"
            >
              <InputText
                id="input-workspace-name"
                v-model="nameInput"
                :disabled="!isOwner || saving"
                class="flex-1"
              />
            </span>
            <Button
              severity="primary"
              :label="saving ? $t('common.saving') : $t('workspace.settings.saveButton')"
              :loading="saving"
              :disabled="!isOwner || saving || nameInput.trim() === workspaceName || !nameInput.trim()"
              @click="saveName"
            >
              <template #icon>
                <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">save</span>
              </template>
            </Button>
          </div>
          <p v-if="!isOwner" class="mt-3 text-xs text-gray-500 flex items-center gap-1">
            <span class="material-icons text-sm">lock</span>
            {{ $t('workspace.settings.ownerOnlyTooltip') }}
          </p>
        </div>

        <!-- Inline error feedback (Billing pattern) -->
        <div
          v-if="nameError"
          class="mt-3 bg-red-900/30 border border-red-700/60 rounded-lg p-3 flex items-center gap-2"
        >
          <span class="material-icons text-red-400 text-base">error_outline</span>
          <p class="text-red-300 text-sm">{{ nameError }}</p>
        </div>
      </div>

      <!-- Card 2: Members -->
      <WorkspaceMembersCard :workspace-id="workspaceId" />

      <!-- Card: Leave workspace (members, non-owners) -->
      <div v-if="!isOwner" class="bg-gray-800 rounded-lg p-6">
        <div class="flex items-center gap-3 mb-4">
          <span class="material-icons text-2xl text-amber-400">logout</span>
          <h2 class="text-lg font-semibold text-white">{{ $t('workspace.settings.leaveTitle') }}</h2>
        </div>
        <p class="text-gray-400 text-sm mb-4">{{ $t('workspace.settings.leaveDescription') }}</p>

        <div class="bg-amber-900/15 border border-amber-700/40 rounded-lg p-4">
          <div class="flex items-start gap-3">
            <span class="material-icons text-amber-400 text-base mt-0.5">info</span>
            <div class="flex-1 min-w-0">
              <p class="text-amber-100 text-sm mb-3">{{ $t('workspace.settings.leaveWarning') }}</p>
              <Button
                severity="warn"
                :label="leaving ? $t('workspace.settings.leaving') : $t('workspace.settings.leaveButton')"
                :loading="leaving"
                :disabled="leaving"
                @click="onLeaveClick"
              >
                <template #icon>
                  <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">logout</span>
                </template>
              </Button>
              <p v-if="leaveError" class="mt-3 text-red-300 text-sm">{{ leaveError }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Card 3: Delete (admin/partner only) -->
      <div v-if="canCreateWorkspaces" class="bg-gray-800 rounded-lg p-6">
        <div class="flex items-center gap-3 mb-4">
          <span class="material-icons text-2xl text-red-400">delete_forever</span>
          <h2 class="text-lg font-semibold text-white">{{ $t('workspace.settings.deleteTitle') }}</h2>
        </div>
        <p class="text-gray-400 text-sm mb-4">{{ $t('workspace.settings.deleteDescription') }}</p>

        <!-- Danger inner panel -->
        <div class="bg-red-900/20 border border-red-700/60 rounded-lg p-4">
          <div class="flex items-start gap-3">
            <span class="material-icons text-red-400 text-base mt-0.5">warning</span>
            <div class="flex-1 min-w-0">
              <p class="text-red-200 text-sm mb-3">
                {{ isLastWorkspace
                  ? $t('workspace.settings.cannotDeleteLast')
                  : $t('workspace.settings.deleteDescription') }}
              </p>
              <!-- The tooltip sits on a wrapper: a disabled button swallows hover events. -->
              <span
                v-tooltip.top="deleteDisabledReason"
                class="inline-flex"
                :class="{ 'disabled-wrap': !isOwner || isLastWorkspace }"
              >
                <Button
                  severity="danger"
                  :label="$t('workspace.settings.deleteButton')"
                  :disabled="!isOwner || isLastWorkspace"
                  @click="openDeleteModal"
                >
                  <template #icon>
                    <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">delete_forever</span>
                  </template>
                </Button>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <DeleteWorkspaceConfirmModal
      v-model="deleteModalOpen"
      :workspace-id="workspaceId"
      :workspace-name="workspaceName"
      @deleted="onWorkspaceDeleted"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useUser } from '@clerk/vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useConfirm } from 'primevue/useconfirm';
import api from '../services/api';
import { useWorkspaceStore } from '../stores/workspaceStore';
import WorkspaceMembersCard from '../components/workspace/WorkspaceMembersCard.vue';
import DeleteWorkspaceConfirmModal from '../components/workspace/DeleteWorkspaceConfirmModal.vue';
import BackButton from '../components/ui/BackButton.vue';
import { useUserRole } from '../composables/useUserRole';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const confirm = useConfirm();
const { user } = useUser();
const workspaceStore = useWorkspaceStore();
const { workspaces } = storeToRefs(workspaceStore);
const { canCreateWorkspaces } = useUserRole();

const workspaceId = computed(() => route.params.workspaceId);

const workspace = ref(null);
const workspaceName = computed(() => workspace.value?.name || '');
const nameInput = ref('');
const loading = ref(false);
const saving = ref(false);
const nameSaved = ref(false);
const nameError = ref('');

const currentUserEmail = computed(() => {
  return user.value?.primaryEmailAddress?.emailAddress || user.value?.email || '';
});

const isOwner = computed(() => {
  const owner = workspace.value?.ownerId;
  if (!owner || !owner.email || !currentUserEmail.value) return false;
  return owner.email.toLowerCase() === currentUserEmail.value.toLowerCase();
});

const isLastWorkspace = computed(() => workspaces.value.length <= 1);

const deleteDisabledReason = computed(() => {
  if (!isOwner.value) return t('workspace.settings.ownerOnlyTooltip');
  if (isLastWorkspace.value) return t('workspace.settings.cannotDeleteLast');
  return '';
});

const fetchWorkspace = async () => {
  const id = workspaceId.value;
  if (!id) return;
  loading.value = true;
  try {
    const res = await api.get(`/workspaces/${id}`);
    if (res.data?.success) {
      workspace.value = res.data.data;
      nameInput.value = workspace.value.name || '';
    }
  } finally {
    loading.value = false;
  }
};

const saveName = async () => {
  saving.value = true;
  nameError.value = '';
  nameSaved.value = false;
  try {
    const res = await api.put(`/workspaces/${workspaceId.value}`, { name: nameInput.value.trim() });
    if (res.data?.success) {
      workspace.value = { ...workspace.value, name: res.data.data.name };
      await workspaceStore.loadWorkspaces();
      nameSaved.value = true;
      setTimeout(() => { nameSaved.value = false; }, 5000);
    }
  } catch (err) {
    nameError.value = err.response?.data?.error?.message || t('common.error');
  } finally {
    saving.value = false;
  }
};

const deleteModalOpen = ref(false);
const openDeleteModal = () => { deleteModalOpen.value = true; };

const onWorkspaceDeleted = async ({ warnings }) => {
  await workspaceStore.loadWorkspaces();
  const next = workspaces.value[0];
  const target = next
    ? { name: 'Bots', params: { workspaceId: next._id } }
    : '/login';

  if (warnings && warnings.length > 0) {
    Console.warn('[WorkspaceSettings] delete warnings:', warnings);
    try {
      sessionStorage.setItem('workspaceDeleteWarnings', JSON.stringify(warnings));
    } catch { /* ignore quota / private mode */ }
  }

  router.push(target);
};

const leaving = ref(false);
const leaveError = ref('');

const onLeaveClick = () => {
  if (leaving.value) return;
  confirm.require({
    header: t('workspace.settings.leaveTitle'),
    message: t('workspace.settings.leaveConfirm', { name: workspaceName.value }),
    icon: 'warning',
    acceptProps: { severity: 'danger', label: t('workspace.settings.leaveButton') },
    rejectProps: { severity: 'secondary', label: t('common.cancel') },
    defaultFocus: 'reject',
    accept: leaveWorkspace,
  });
};

const leaveWorkspace = async () => {
  if (leaving.value) return;
  leaving.value = true;
  leaveError.value = '';
  try {
    await workspaceStore.leaveWorkspace(workspaceId.value);
    const next = workspaces.value[0];
    if (next) {
      router.push({ name: 'Bots', params: { workspaceId: next._id } });
    } else {
      router.push('/login');
    }
  } catch (err) {
    leaveError.value = err.response?.data?.error?.message || t('workspace.settings.leaveErrorGeneric');
  } finally {
    leaving.value = false;
  }
};

watch(workspaceId, fetchWorkspace);
onMounted(async () => {
  if (workspaces.value.length === 0) {
    await workspaceStore.loadWorkspaces();
  }
  await fetchWorkspace();
});
</script>

<style scoped>
/*
 * A disabled control swallows hover events, so its v-tooltip lives on a wrapper
 * span. While the control is disabled, it lets the pointer through to the
 * wrapper (same pattern as CustomizeChatroom's .save-btn-wrap).
 */
.disabled-wrap { cursor: not-allowed; }
.disabled-wrap :deep(.p-button),
.disabled-wrap :deep(.p-inputtext) { pointer-events: none; }
</style>
